import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider, type HelmetServerState } from "react-helmet-async";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useLocation, Redirect } from "wouter";
import { lazy, Suspense, useEffect } from "react";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ElevenLabsWidgetGate } from "./components/ElevenLabsWidget";
import { GooglePreferredSourcesGate } from "./components/GooglePreferredSources";
import { DivergenceLaunchPopup } from "./components/DivergenceLaunchPopup";

// Critical routes - loaded eagerly (home, pricing, product, about, testimonials, contact, support)
import {
  StructuraAboutPage,
  StructuraContactPage,
  StructuraHomePage,
  StructuraPricingPage,
  StructuraProductPage,
  StructuraSupportPage,
  StructuraTestimonialsPage,
} from "@/pages/StructuraPages";

// Non-critical routes - lazy loaded (separate JS chunks, not downloaded on home page)
const NotFound = lazy(() => import("@/pages/NotFound"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Blog = lazy(() => import("@/pages/Blog"));
const Education = lazy(() => import("@/pages/Education"));
const Hero2 = lazy(() => import("@/pages/Hero2"));
const StructuraManualPage = lazy(() => import("@/pages/StructuraManualPage"));
const TrendGoIsNowStructura = lazy(() => import("@/pages/TrendGoIsNowStructura"));
const BlackFriday = lazy(() => import("@/pages/BlackFriday"));
const FreeToolsPage = lazy(() => import("@/pages/FreeToolsPage"));
const VtadPage = lazy(() => import("@/pages/VtadPage"));
const MeetPrism = lazy(() => import("@/pages/MeetPrism"));
const FreeTradingViewIndicatorsPage = lazy(() => import("@/pages/FreeTradingViewIndicatorsPage"));
const ReadIndexPage = lazy(() => import("@/pages/ReadPage").then(module => ({ default: module.ReadIndexPage })));
const ReadArticlePage = lazy(() => import("@/pages/ReadPage").then(module => ({ default: module.ReadArticlePage })));
const ToolsHubPage = lazy(() => import("@/pages/ToolsPage").then(module => ({ default: module.ToolsHubPage })));
const ToolDetailPage = lazy(() => import("@/pages/ToolsPage").then(module => ({ default: module.ToolDetailPage })));
const DeskPage = lazy(() => import("@/pages/desk/DeskPage").then(module => ({ default: module.DeskPage })));

// Minimal loading fallback - dark bg to avoid flash of white
function PageLoader() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#020617",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          border: "2px solid rgba(51,205,211,0.2)",
          borderTopColor: "#33CDD3",
          borderRadius: "50%",
          animation: "spin 0.7s linear infinite",
        }}
      />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined" && !window.location.hash) {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [location]);
  return null;
}

function Router() {
  // make sure to consider if you need authentication for certain routes
  return (
    <Suspense fallback={<PageLoader />}>
      <ScrollToTop />
      <Switch>
        <Route path="/" component={StructuraHomePage} />
        <Route path="/pricing" component={StructuraPricingPage} />
        <Route path="/product" component={StructuraProductPage} />
        <Route path="/about" component={StructuraAboutPage} />
        <Route path="/testimonials" component={StructuraTestimonialsPage} />
        <Route path="/contact" component={StructuraContactPage} />
        <Route path="/support" component={StructuraSupportPage} />
        <Route path="/faq" component={StructuraSupportPage} />
        <Route path="/terms" component={Terms} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/blog"><Redirect to="/education" /></Route>
        <Route path="/education" component={Education} />
        <Route path="/education/meet-prism" component={MeetPrism} />
        <Route path="/education/free-tradingview-indicators-market-structure" component={FreeTradingViewIndicatorsPage} />
        <Route path="/hero2" component={Hero2} />
        <Route path="/manual" component={StructuraManualPage} />
        <Route path="/trendgo-is-now-structura" component={TrendGoIsNowStructura} />
        <Route path={"/black-friday"} component={BlackFriday} />
        <Route path="/free-tools" component={FreeToolsPage} />
        <Route path="/tools" component={ToolsHubPage} />
        <Route path="/tools/:slug" component={ToolDetailPage} />
        <Route path="/vtad" component={VtadPage} />
        <Route path="/desk" component={DeskPage} />
        <Route path="/read" component={ReadIndexPage} />
        <Route path="/read/:slug" component={ReadArticlePage} />
        <Route path={"/404"} component={NotFound} />
        {/* Final fallback route */}
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

// NOTE: About Theme
// - First choose a default theme according to your design style (dark or light bg), than change color palette in index.css
//   to keep consistent foreground/background color across components
// - If you want to make theme switchable, pass `switchable` ThemeProvider and use `useTheme` hook

interface AppProps {
  helmetContext?: { helmet?: HelmetServerState | null };
}

function App({ helmetContext }: AppProps) {
  return (
    <HelmetProvider context={helmetContext}>
      <ErrorBoundary>
        <ThemeProvider defaultTheme="dark">
          <TooltipProvider>
            <Toaster />
            <Router />
            <ElevenLabsWidgetGate />
            <GooglePreferredSourcesGate />
            <DivergenceLaunchPopup />
          </TooltipProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
